import { useCallback, useMemo, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { LucideIcon } from 'lucide-react';
import { Info, Monitor, Smartphone, Tablet } from 'lucide-react';
import type { TechnicalDistribution } from '../types';

type DeviceConfigEntry = { color: string; bg: string; icon: LucideIcon; labelKey: string };

const DEVICE_CONFIG: Record<string, DeviceConfigEntry> = {
  Desktop: { color: '#2C6CFF', bg: 'bg-[#EBF1FF]', icon: Monitor, labelKey: 'devices.desktop' },
  Mobile: { color: '#FF6526', bg: 'bg-[#FF652620]', icon: Smartphone, labelKey: 'devices.mobile' },
  Tablet: { color: '#B94746', bg: 'bg-[#B9474620]', icon: Tablet, labelKey: 'devices.tablet' },
  Other: { color: '#9E9FA6', bg: 'bg-[#F3F4F6]', icon: Info, labelKey: 'devices.other' },
};

const BADGE_PX = 32;
const BADGE_OFFSET = 6;

type SliceLabelProps = {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  index?: number;
  payload?: {
    count?: number;
    config?: DeviceConfigEntry;
  };
};

function SliceOuterIconBadge(sliceProps: SliceLabelProps) {
  const { cx = 0, cy = 0, outerRadius = 0, startAngle = 0, endAngle = 0, payload } = sliceProps;

  const Icon = payload?.config?.icon;
  if (!Icon || !payload?.count) {
    return null;
  }

  const span = endAngle - startAngle;
  if (span < 0.5) {
    return null;
  }

  const midAngleDeg = (startAngle + endAngle) / 2;
  const RADIAN = Math.PI / 180;
  const r = outerRadius + BADGE_OFFSET - BADGE_PX * 0.35;
  const x = cx + Math.cos(-RADIAN * midAngleDeg) * r;
  const y = cy + Math.sin(-RADIAN * midAngleDeg) * r;
  const half = BADGE_PX / 2;

  return (
    <g style={{ pointerEvents: 'none' }}>
      <foreignObject
        x={x - half}
        y={y - half}
        width={BADGE_PX}
        height={BADGE_PX}
        style={{ overflow: 'visible' }}
      >
        {createElement(
          'div',
          {
            xmlns: 'http://www.w3.org/1999/xhtml',
            className:
              'flex size-10 items-center justify-center rounded-full border border-[#E8E9ED] bg-white',
          },
          createElement(Icon, {
            size: 20,
            strokeWidth: 2.25,
            className: 'text-[#0C0E21]',
          }),
        )}
      </foreignObject>
    </g>
  );
}

export const DeviceDonutChart = ({ data }: { data: TechnicalDistribution | null }) => {
  const { t } = useTranslation('analytics');
  const chartData = useMemo(() => {
    if (!data?.devices) return [];
    return data.devices.map((d) => {
      const config = DEVICE_CONFIG[d.label] || DEVICE_CONFIG['Other'];
      return {
        ...d,
        config,
        fill: config.color,
      };
    });
  }, [data]);

  const renderSliceLabel = useCallback(
    (props: SliceLabelProps & { index?: number }) => {
      const row =
        props.payload && 'config' in props.payload ? props.payload : chartData[props.index ?? 0];
      return <SliceOuterIconBadge {...props} payload={row} />;
    },
    [chartData],
  );

  if (!data || chartData.length === 0) return null;

  return (
    <div className="flex h-[480px] w-full min-w-0 flex-col rounded-[20px] border border-medium bg-[#FAFAFB] p-6 shadow-sm xl:h-full">
      <h3 className="mb-3 text-center text-sm font-medium text-black">{t('devices.title')}</h3>

      <div className="relative min-h-0 w-full flex-1 overflow-visible">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              innerRadius="60%"
              outerRadius="90%"
              paddingAngle={6}
              dataKey="count"
              stroke="none"
              isAnimationActive={true}
              label={renderSliceLabel}
              labelLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-1">
        {chartData.map((item, idx) => (
          <div key={idx} className="group w-full flex items-center justify-between">
            <div className="flex items-start gap-3 w-full">
              <div className={`rounded-lg p-2 ${item.config.bg}`}>
                <item.config.icon size={14} color={item.config.color} />
              </div>
              <div className="flex flex-col">
                <span className="text-[13px] font-semibold text-[#6D6E7A]">
                  {t(item.config.labelKey)}
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm font-bold text-dark">{item.count}</span>
                  <span className="text-[10px] font-medium text-[#6D6E7A]">
                    ({item.percentage}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload }: any) => {
  const { t } = useTranslation('analytics');
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="z-[9999] rounded-lg border border-white/10 bg-dark px-3 py-2 text-[11px] font-medium text-white shadow-xl">
        {t(d.config.labelKey)} : <span className="ml-1 font-bold text-accent">{d.count}</span>
      </div>
    );
  }
  return null;
};
